import type { ReactNode } from 'react';

import { useSession } from '@/app/session';
import { PageHeader } from '@/shared/layout/page-header';
import { CatalogueSummaryWidget } from '@/modules/dashboard/widgets/catalogue-summary';
import { EntitlementsSummaryWidget } from '@/modules/dashboard/widgets/entitlements-summary';
import { MediaSummaryWidget } from '@/modules/dashboard/widgets/media-summary';
import { OffersSummaryWidget } from '@/modules/dashboard/widgets/offers-summary';
import { OperationsControlCentre } from '@/modules/dashboard/widgets/operations-control-centre';
import { RecentActivityWidget } from '@/modules/dashboard/widgets/recent-activity';
import { SetupCompleteness } from '@/modules/dashboard/widgets/setup-completeness';
import { StockSummaryWidget } from '@/modules/dashboard/widgets/stock-summary';
import { TeamSummaryWidget } from '@/modules/dashboard/widgets/team-summary';
import { TenantRegistry } from '@/modules/dashboard/widgets/tenant-registry';
import { VendorOperations } from '@/modules/dashboard/widgets/vendor-operations';

/**
 * THE TWO AUTHORITY AXES, AND WHY THE PAGE HAS TWO SURFACES
 * -------------------------------------------------------
 * The API resolves TWO independent kinds of authority, and this page keeps them apart:
 *
 *   - `permissions` — the TENANT authority of the actor's membership role. This is the
 *     tenant's own workspace: its offers, its stock, its media, its team.
 *   - `platformPermissions` — the actor's LIVE platform grants on this deployment. This is
 *     deployment-wide authority: the tenant registry, the operations control centre.
 *
 * They are not a superset and are never merged. A platform administrator is often a `viewer`
 * in one tenant — so the platform surface is rich and the tenant surface is thin — while a
 * merchant owner is rich in the tenant surface and holds no platform grant at all. Rendering
 * ONE surface for both would show each of them a page that is mostly refused.
 *
 * So: the tenant surface is always rendered, and the platform surface is rendered when the
 * actor holds platform authority. This mirrors `composeNavigation`'s rule in
 * `src/app/config/navigation.ts`, which picks the axis by `permissionScope` and never mixes
 * them.
 *
 * ---------------------------------------------------------------------------
 * A WIDGET IS RENDERED ONLY WHEN THE ACTOR MAY READ IT
 * ---------------------------------------------------------------------------
 * Every widget's read is refused by the server for an actor without its permission. Rendering
 * the widget anyway would put a refusal on the page — which reads as a fault rather than as an
 * absence, and teaches the operator to distrust the dashboard.
 *
 * These checks are a UX AFFORDANCE, not authorization: the server re-authorizes every request
 * anyway, and the widgets themselves render the shared refused state if the server says no.
 */

/** The permission each tenant-surface widget's read needs. */
const TENANT_WIDGETS = {
  setup: 'vendor.profile.read',
  offers: 'catalog.offer.read',
  stock: 'inventory.stock.read',
  movements: 'inventory.stock.read',
  media: 'content.mediaasset.read',
  team: 'workforce.staff.read',
  entitlements: 'entitlement.read',
} as const;

/** The permission each platform-surface widget's read needs. */
const PLATFORM_WIDGETS = {
  tenants: 'tenant.read',
  backlog: 'operations.backlog.read',
  vendor: 'operations.vendor.summary.read',
} as const;

/**
 * The permission codes of ONE axis.
 *
 * The two surfaces never share a list, so they never share a parameter either — which is the
 * point. Handing the tenant surface the platform codes would be a way of merging the axes by
 * accident, and the whole design rests on their being independent.
 */
export type DashboardSurfaceProps = {
  readonly permissions: ReadonlyArray<string>;
};

/**
 * The tenant's own workspace.
 *
 * Rendered for EVERY signed-in actor, because every actor has a tenant. Which widgets it shows
 * depends on the actor's TENANT permissions, and on nothing else.
 */
function TenantSurface({ permissions }: DashboardSurfaceProps): ReactNode {
  return (
    // `items-start` so each widget takes its OWN height. Without it a grid stretches every
    // cell to the tallest one in its row, and a short widget sits in a field of empty
    // space that reads as a rendering fault.
    <section aria-label="This workspace" className="grid items-start gap-4 lg:grid-cols-2">
      {permissions.includes(TENANT_WIDGETS.setup) ? <SetupCompleteness /> : null}
      {permissions.includes(TENANT_WIDGETS.offers) ? <OffersSummaryWidget /> : null}
      {/* The canonical catalogue is readable by ANY signed-in actor — the API declares no
          permission for it — so it is shown to everyone rather than gated on a code that does
          not exist. */}
      <CatalogueSummaryWidget />
      {permissions.includes(TENANT_WIDGETS.stock) ? (
        <StockSummaryWidget canReadOffers={permissions.includes(TENANT_WIDGETS.offers)} />
      ) : null}
      {permissions.includes(TENANT_WIDGETS.movements) ? <RecentActivityWidget /> : null}
      {permissions.includes(TENANT_WIDGETS.media) ? <MediaSummaryWidget /> : null}
      {permissions.includes(TENANT_WIDGETS.team) ? <TeamSummaryWidget /> : null}
      {permissions.includes(TENANT_WIDGETS.entitlements) ? <EntitlementsSummaryWidget /> : null}
    </section>
  );
}

/**
 * The platform's deployment-wide surface.
 *
 * Rendered only for an actor who holds a platform grant. A tenant administrator sees no
 * platform section at all — not a platform section full of refusals.
 */
function PlatformSurface({ permissions }: DashboardSurfaceProps): ReactNode {
  const holdsAnyPlatformGrant = Object.values(PLATFORM_WIDGETS).some((code) =>
    permissions.includes(code),
  );

  if (!holdsAnyPlatformGrant) return null;

  return (
    <section aria-label="Platform" className="grid items-start gap-4 lg:grid-cols-2">
      {permissions.includes(PLATFORM_WIDGETS.tenants) ? <TenantRegistry /> : null}
      {permissions.includes(PLATFORM_WIDGETS.backlog) ? <OperationsControlCentre /> : null}
      {permissions.includes(PLATFORM_WIDGETS.vendor) ? <VendorOperations /> : null}
    </section>
  );
}

export function DashboardOverviewPage(): ReactNode {
  const session = useSession();
  const tenantPermissions = session.user?.permissions ?? [];
  const platformPermissions = session.user?.platformPermissions ?? [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dashboard"
        description="What this workspace holds, and what the platform has open against it."
      />

      <TenantSurface permissions={tenantPermissions} />
      <PlatformSurface permissions={platformPermissions} />
    </div>
  );
}
